/* ==========================================================================
   WAYPOINT — theme resolution. Three user-facing choices (Settings screen):

     'light'  — always the warm cream paper
     'dark'   — always the after-sundown palette
     'auto'   — "Follow the time": cream by day, dark after sunset

   'auto' deliberately follows the CLOCK, not `prefers-color-scheme`. This
   is a trip journal that gets read from a camp chair — the honest signal for
   "it's dark out" is what time it is where the rig is parked, not whatever
   the phone's OS-level appearance schedule happens to say. (The OS
   preference is still the pre-hydration fallback in tokens.css, so the very
   first paint of a cold open isn't a cream flash on a phone in night mode.)

   Sunrise/sunset are approximated from the device's own latitude only if we
   already have it for free; we don't, so this uses fixed local hours — see
   DAY_START_HOUR / DAY_END_HOUR. Deliberately dumb and dependency-free: the
   difference between "sunset at 19:40" and "dark from 19:00" is not worth a
   geolocation prompt or a solar-position library on this screen.
   ========================================================================== */

export type ThemeChoice = 'light' | 'dark' | 'auto';
export type ResolvedTheme = 'light' | 'dark';

const THEME_KEY = 'waypoint:theme';

/** Local hours [DAY_START_HOUR, DAY_END_HOUR) render light in 'auto'. */
const DAY_START_HOUR = 7;
const DAY_END_HOUR = 19;

export function loadThemeChoice(): ThemeChoice {
  if (typeof localStorage === 'undefined') return 'auto';
  const v = localStorage.getItem(THEME_KEY);
  return v === 'light' || v === 'dark' || v === 'auto' ? v : 'auto';
}

export function persistThemeChoice(choice: ThemeChoice): void {
  try {
    localStorage.setItem(THEME_KEY, choice);
  } catch {
    // storage unavailable (private mode) — in-memory choice still applies
  }
}

/** What 'auto' resolves to right now. */
export function themeForNow(now: Date = new Date()): ResolvedTheme {
  const h = now.getHours();
  return h >= DAY_START_HOUR && h < DAY_END_HOUR ? 'light' : 'dark';
}

export function resolveTheme(choice: ThemeChoice, now: Date = new Date()): ResolvedTheme {
  return choice === 'auto' ? themeForNow(now) : choice;
}

/** Stamp the resolved theme on <html> so tokens.css's `[data-theme]` blocks
 *  take over from the `prefers-color-scheme` fallback. Always writes an
 *  EXPLICIT light/dark (never removes the attribute) — under 'auto' the
 *  clock and the OS preference can legitimately disagree, and the clock is
 *  the one this app promised. */
export function applyTheme(theme: ResolvedTheme): void {
  if (typeof document === 'undefined') return;
  document.documentElement.dataset.theme = theme;
}

/** Milliseconds until the next 'auto' flip, so the app can schedule exactly
 *  one timer instead of polling. Clamped to a minute so a clock change (or a
 *  rounding edge right on the hour) can never spin. */
export function msUntilNextThemeFlip(now: Date = new Date()): number {
  const boundaryHour = themeForNow(now) === 'light' ? DAY_END_HOUR : DAY_START_HOUR;
  const next = new Date(now);
  next.setHours(boundaryHour, 0, 0, 0);
  if (next.getTime() <= now.getTime()) next.setDate(next.getDate() + 1);
  return Math.max(60_000, next.getTime() - now.getTime());
}
